import { useEffect, useState, useRef } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { TEAM as key } from 'constants/keys';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectSaga } from 'utils/injectSaga';
import reducer from './reducer';
import saga from './saga';
import { getTeamDataRequest, getCostReportRequest, getMonthlyReportRequest } from "./action";
import { selectTeamDataList, selectIsLoading, selectAttempts, selectCostReportData, selectIsCostReportLoading, selectCostReportError, selectMonthlyReportData, selectIsMonthlyReportLoading } from "./selector";
import SubFooterComponent from "components/SubFooterComponent";
import TeamTableComponent from 'components/TeamTableComponent';
import { Container, TopContainer, TeamTableContainer } from "./styled";
import TeamChartComponent from 'components/TeamChartComponent';
import { TeamOutlined } from '@ant-design/icons';
import ProgressSubHeader from 'components/ProgressSubHeader';
import { uppercaseFirst } from 'utils';
import { Table, Select, Checkbox, DatePicker, Button, Row, Col } from 'antd';
import { Card, Statistic, Spin } from 'antd';
import { DollarOutlined } from '@ant-design/icons';

const defaultConfig = {
    data: [],
    xField: "type",
    yField: "value",
    seriesField: "",
    legend: false,
}

const printStyle = `
    @media print {
        @page {
            size: A4 landscape;
            margin: 10mm;
        }  
        .ant-table-thead > tr > th,
        .ant-table-tbody > tr > td {
            font-size: 16px;
        }
    }
`

const { Option } = Select;
const { RangePicker } = DatePicker;

const AggregationType = {
    0: 'Daily',
    1: 'Weekly',
    2: 'Monthly',
    3: 'Yearly',
};

const TeamProgressContainer = (props) => {
    const { getTeamDataRequest, getCostReportRequest, getMonthlyReportRequest, teamDataList, costReportData, isCostReportLoading, costReportError, monthlyReportData, isMonthlyReportLoading } = props;
   
    const [averageScoreChartConfig, setAverageScoreChartConfig] = useState(defaultConfig);
    const [overallTimeChartConfig, setOverallTimeChartConfig] = useState(defaultConfig);
    const [tableData, setTableData] = useState([]);
    const [isSelfLoading, setIsSelfLoading] = useState(false);
    const printRef = useRef(null);
    const [costReportChartConfig, setCostReportChartConfig] = useState(defaultConfig);
    const [aggregationType, setAggregationType] = useState(0);
    const [includeUserId, setIncludeUserId] = useState(false);
    const [includeTrainingId, setIncludeTrainingId] = useState(false);
    const [dateRange, setDateRange] = useState([]);

    useInjectReducer({key, reducer});
	useInjectSaga({key, saga});

    useEffect(() => {
            getTeamDataRequest();
            getMonthlyReportRequest();
    }, [getTeamDataRequest, getMonthlyReportRequest]);

    useEffect(() => {
        const payload = {
            aggregationType,
            startDate: dateRange[0]?.format('YYYY-MM-DD'),
            endDate: dateRange[1]?.format('YYYY-MM-DD'),
        }
        if(includeUserId) {
            payload.includeUserId = true;
        }
        if(includeTrainingId) {
            payload.includeTrainingId = true;
        }
        getCostReportRequest(payload);
    }, [getCostReportRequest, aggregationType, dateRange, includeUserId, includeTrainingId]);

    useEffect(() => {
        // creating cost report chart
        // only take last 12 items
        const costReportChartData = costReportData.slice(-5).map((e, index) => {
            return {
                // format date with locale string
                date: new Date(e.date).toLocaleDateString(),
                cost: e.cost,
                method: "Cost"
            }
        }).reverse();

        const costReportChartConfig = {
            data: costReportChartData,
            xField: "date",
            yField: "cost",
            seriesField: "method",
            label: {
                style: {
                    fill: '#aaa',
                },
                formatter: (value) => {
                    return `$${value.cost.toFixed(2)}`;
                }
            },
        }   
        setCostReportChartConfig(costReportChartConfig);
    }, [costReportData]);

    useEffect(() => {
        console.log("teamDataList", teamDataList);
        const overallTimePerUserData = teamDataList.map((e, index) => { 
            return {
            id: e.lastTrainingAttemptId,
            method: "Minutes Spent Training",
            type: uppercaseFirst(e.username),
            // round up to the nearest integer
            value: Math.ceil(e.totalTrainingTime/60)
        } 
        });

        console.log("overallTimePerUserData", overallTimePerUserData);
        const averageScorePerUserData = teamDataList.map((e, index) => { 
            return {
            id: e.lastTrainingAttemptId,
            method: "Average Training Score",
            type: uppercaseFirst(e.username),
            value: parseFloat(e.averageScore.toFixed(2))
        } });
        
        const averageUserScoreChartConfig = {
            data: averageScorePerUserData,
            isGroup: true,
            xField: "type",
            yField: "value",
            seriesField: "method",
            legend: true,
            label: {
                style: {
                    fill: '#aaa',
                },
                formatter: (value) => {
                    return `${value.value.toFixed(0)}%`;
                }
            },
            color:['#5ECCF3','#4E67C8', '#A7EA52', '#5DCEAF'],
        };
        const overallTimeChartConfig = {
            data: overallTimePerUserData,
            isGroup: true,
            xField: "type",
            yField: "value",
            seriesField: "method",
            legend: true,
            label: {
                style: {
                    fill: '#aaa',
                },
                formatter: (value) => {
                    return `${value.value.toFixed(0)}`;
                }
            },
            color:['#4E67C8','#5ECCF3', '#A7EA52', '#5DCEAF'],
        };
        // newConfig.data = chartData;

        const tableData = teamDataList.map((e, index) => { return {
            ...e, USER_NUM: index + 1, USER_ID: uppercaseFirst(e.username)
        } });
        setTableData(tableData);

        setAverageScoreChartConfig(averageUserScoreChartConfig);
        setOverallTimeChartConfig(overallTimeChartConfig);
        setIsSelfLoading(true);
    }, [teamDataList])

    const handleReloadData = () => {
        getTeamDataRequest();
        getCostReportRequest();
    }

    const currentMonthCost = monthlyReportData[monthlyReportData.length - 1]?.cost || 0;
    const previousMonthCost = monthlyReportData[monthlyReportData.length - 2]?.cost || 0;

    return (
        <Container>
            
            <ProgressSubHeader 
                title={"Training Dashboard"} 
                printRef={printRef} 
                style={{fontSize: "24px", fontWeight: 1000}} 
                icon={<TeamOutlined />}
                printStyle={printStyle}
                handleReloadData={handleReloadData}
            />
            <TopContainer>

                <Row>
                    <Col span={24}>
                        <Select 
                            style={{margin: 16}}
                            value={aggregationType} 
                            onChange={(value) => setAggregationType(value)}
                        >
                            <Select.Option value={0}>Daily</Select.Option>
                            <Select.Option value={1}>Weekly</Select.Option>
                            <Select.Option value={2}>Monthly</Select.Option>
                            <Select.Option value={3}>Yearly</Select.Option>
                        </Select>

                    </Col>
                </Row>
               
                <Row style={{marginBottom: 16}}>   
                    <Col span={24} lg={8}>
                        <TeamChartComponent config={costReportChartConfig} isLoading={isCostReportLoading}/>
                    </Col>
                    <Col span={24} lg={8}>
                        <TeamChartComponent config={averageScoreChartConfig} isLoading={isSelfLoading}/>
                    </Col>
                    <Col span={24} lg={8}>
                        <TeamChartComponent config={overallTimeChartConfig} isLoading={isSelfLoading}/>
                    </Col>
                </Row>
                <Row gutter={8} style={{margin: 4}}>
                    <Col span={24} lg={12}>
                        <Card>
                            <Spin spinning={isMonthlyReportLoading}>
                                <Statistic
                                    title="Month to Date Cost"
                                    value={currentMonthCost}
                                    precision={2}
                                    prefix={<DollarOutlined />}
                                />
                            </Spin>
                        </Card>
                    </Col>
                    <Col span={24} lg={12}>
                        <Card>
                            <Spin spinning={isMonthlyReportLoading}>
                                <Statistic
                                    title="Previous Month Cost"
                                    value={previousMonthCost}
                                    precision={2}
                                    prefix={<DollarOutlined />}
                                />
                            </Spin>
                        </Card>
                    </Col>
                </Row>                                 
                <Row gutter={16}>
                    <TeamTableComponent datas={tableData} isLoading={!isSelfLoading} handleReloadData={handleReloadData}/>
                </Row>

                <SubFooterComponent />
            </TopContainer>
        </Container>
    );
}

const mapStateToProps = createStructuredSelector({
    isLoading: selectIsLoading(),
    teamDataList: selectTeamDataList(),
    attempts: selectAttempts(),
    costReportData: selectCostReportData(),
    isCostReportLoading: selectIsCostReportLoading(),
    costReportError: selectCostReportError(),
    monthlyReportData: selectMonthlyReportData(),
    isMonthlyReportLoading: selectIsMonthlyReportLoading(),
});

const mapDispatchToProps = (dispatch) => ({
    getTeamDataRequest: (data) => dispatch(getTeamDataRequest(data)), 
    getCostReportRequest: (data) => dispatch(getCostReportRequest(data)),
    getMonthlyReportRequest: () => dispatch(getMonthlyReportRequest()),
});

export default connect(mapStateToProps, mapDispatchToProps)(TeamProgressContainer);